import { CSSProperties, ReactNode, useRef } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Alert, Button } from "reactstrap";
import { Drawer } from "rsuite";
import IconButton from "../../elements/IconButton";
import { CrashViewComponent } from "../../views/other/CrashView";

export type Placement = "top" | "bottom" | "right" | "left";
export type DrawerSize = "full" | "lg" | "md" | "sm" | "xs";

const defaultBodyStyle: CSSProperties = {};

export interface DrawerError {
  message: string;
  onClear?: () => void;
}

export interface DrawerSuccess {
  message: string;
  onClear: () => void;
}

interface AppDrawerProps {
  placement?: Placement;
  size?: DrawerSize;
  full?: boolean;
  backdrop?: boolean | "static";
  open: boolean;
  onClose: (() => void) | undefined;
  onExited?: () => void;
  onEntered?: () => void;
  headerTitle: any | undefined;
  headerDescription?: ReactNode;
  headerOptions?: JSX.Element;
  bodyCSS?: string;
  bodyStyle?: CSSProperties;
  drawerStyle?: CSSProperties;
  body: JSX.Element | ((containerRef: any) => JSX.Element);
  disablePadding?: boolean;
  error?: DrawerError | null;
  info?: string;
  success?: DrawerSuccess | null;
  keyboard?: boolean;
  onOpen?: () => void;
  disableHeader?: boolean;
  headerIcon?: string;
}

const AppDrawer = ({
  placement = "right",
  size = "sm",
  full = false,
  backdrop = true,
  open,
  onClose,
  onExited,
  headerTitle,
  headerDescription,
  headerOptions,
  bodyCSS,
  bodyStyle,
  drawerStyle,
  body,
  disablePadding,
  error, // Expects an object - { message: "The error message", onClear: function }
  info,
  success,
  keyboard = false,
  onOpen,
  onEntered,
  disableHeader,
  headerIcon,
}: AppDrawerProps) => {
  const styleObject = bodyStyle ? bodyStyle : defaultBodyStyle;
  const drawerBodyRef = useRef<any>(null);

  // Add relative positioning so error popup can be placed properly
  if (error || info || success) {
    styleObject.position = "relative";
  }

  // Fix deprecation error
  if (full === true) {
    size = "full";
  }

  return (
    <Drawer
      placement={placement}
      open={open}
      onClose={onClose}
      onEntered={onEntered}
      size={size}
      backdrop={backdrop}
      onExited={onExited ? onExited : undefined}
      keyboard={keyboard}
      style={drawerStyle}
      onOpen={onOpen}
      closeButton={false}
    >
      {!disableHeader && (
        <div
          className="tw:flex tw:justify-between tw:flex-wrap tw:items-center tw:border-b tw:border-gray-200 tw:py-4 tw:pr-4"
          style={{ paddingLeft: 55 }}
        >
          <div className="tw:absolute" style={{ top: 13, left: 13 }}>
            <IconButton icon={headerIcon || "close"} onClick={onClose} />
          </div>
          <div className="tw:flex tw:pb-2 tw:mr-3">
            <div>
              <h2 className="tw:text-dark tw:mb-0" style={{ fontSize: 17 }}>
                {headerTitle}
              </h2>
              <div className="tw:text-sm">{headerDescription}</div>
            </div>
          </div>
          <div>{headerOptions}</div>
        </div>
      )}
      <ErrorBoundary FallbackComponent={CrashViewComponent}>
        <Drawer.Body
          style={styleObject}
          className={`${
            bodyCSS ? bodyCSS : "tw:pb-32"
          } tw:overflow-auto tw:h-[92%]`}
          id={disablePadding ? "rs-drawer-no-pad" : undefined}
          ref={drawerBodyRef}
        >
          {typeof body === "function" ? body(drawerBodyRef) : body}
          {error && (
            <DrawerMessage
              message={error?.message}
              onClear={error?.onClear}
              type="danger"
              icon="alert-circle"
              absolute
            />
          )}
          {info && (
            <DrawerMessage
              message={info}
              type="info"
              icon="information-variant-circle"
              absolute={false}
            />
          )}
          {success && (
            <DrawerMessage
              message={success.message}
              onClear={success?.onClear}
              type="success"
              icon="information-variant-circle"
              absolute={false}
            />
          )}
        </Drawer.Body>
      </ErrorBoundary>
    </Drawer>
  );
};

function DrawerMessage({
  message,
  onClear,
  type,
  icon,
  absolute,
}: DrawerMessageProps) {
  return (
    <Alert
      color={type}
      style={{
        fontSize: 16,
        position: absolute ? "fixed" : undefined,
        bottom: absolute ? 10 : undefined,
        width: absolute ? "calc(100% - 50px)" : "100%",
        zIndex: 999,
      }}
      className="tw:p-3 tw:flex tw:items-center tw:justify-between"
    >
      <div className="tw:flex tw:items-center">
        <i className={`mdi mdi-${icon} tw:mr-4`} style={{ fontSize: 30 }} />
        {message}
      </div>
      {onClear && (
        <Button
          className="tw:w-[2.375rem] tw:h-[2.375rem] tw:p-0"
          size="sm"
          outline
          color="white"
          onClick={onClear}
        >
          <i className="mdi mdi-close tw:text-[26px]" />
        </Button>
      )}
    </Alert>
  );
}

interface DrawerMessageProps {
  message: string;
  onClear?: () => void;
  type: "danger" | "info" | "success";
  icon: string;
  absolute: boolean;
}

export default AppDrawer;
